import { useMemo } from 'react';
import { useGetDegreesQuery, Visibility } from '@teachur/http-client';
import type { TargetDegreeType } from '@teachur/http-client';

import { withForm } from '@new/common/hooks/form/form';
import type { SelectOption } from '@new/common/ui/Select/Select';

import {
  ADDITIONAL_CREDITS_SOURCE_OPTIONS,
  TARGET_DEGREE_OPTIONS,
  situationNeedsAdditionalCredits,
  situationNeedsCredits,
  situationNeedsDegree,
} from '../../../utils/setupOptions';
import { wizardDefaultValues } from '../../../schemas/wizard.schema';
import { TargetProgramSelect } from './TargetProgramSelect';

const SOURCE_SELECT_OPTIONS = ADDITIONAL_CREDITS_SOURCE_OPTIONS.map((o) => ({
  label: o.label,
  value: o.value as string,
}));

export const CreditsInfoStep = withForm({
  defaultValues: wizardDefaultValues,
  render: function CreditsInfoStepRender({ form }) {
    const { data: degrees = [] } = useGetDegreesQuery();

    const programOptionsByType = useMemo((): Record<TargetDegreeType, SelectOption[]> => {
      const build = (type: TargetDegreeType): SelectOption[] =>
        degrees
          .filter((d) => d.visibility === Visibility.Public && d.type === type)
          .map((d) => ({ label: d.name, value: String(d.id) }));
      return {
        associate: build('associate'),
        bachelor: build('bachelor'),
        master: build('master'),
      };
    }, [degrees]);

    return (
      <div className="flex flex-col gap-6">
        <div>
          <h2 className="m-0! [font-size:var(--fs-h5)]! font-bold text-(--neutral-black-NB1000)!">
            Credits Information
          </h2>
          <p className="mt-1 [font-size:var(--fs-p3)]! text-(--neutral-black-NB700)!">
            Tell us about your target program and prior credits.
          </p>
        </div>

        <div className="flex flex-col gap-4">
          <form.AppField
            name="targetDegreeType"
            listeners={{
              onChange: () => form.setFieldValue('targetDegreeId', null),
            }}
          >
            {(field) => <field.Select label="Target degree" options={TARGET_DEGREE_OPTIONS} />}
          </form.AppField>

          <form.Subscribe selector={(s) => s.values.targetDegreeType}>
            {(degree) => (
              <form.AppField name="targetDegreeId">
                {(field) => (
                  <TargetProgramSelect
                    value={field.state.value ?? null}
                    options={programOptionsByType[degree]}
                    errors={field.state.meta.isTouched ? field.state.meta.errors : []}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                  />
                )}
              </form.AppField>
            )}
          </form.Subscribe>
        </div>

        <form.Subscribe selector={(s) => s.values.priorEducationStatus}>
          {(situation) => (
            <div className="flex flex-col gap-4">
              {situationNeedsCredits(situation) ? (
                <>
                  <form.AppField name="estimatedCredits">
                    {(field) => (
                      <field.Number
                        label="Approximately how many college credits?"
                        value={field.state.value ?? ''}
                        onChange={(v: number | string) =>
                          field.handleChange(v === '' ? null : Number(v))
                        }
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="creditSourceCount">
                    {(field) => (
                      <field.Number
                        label="How many different institutions / credit sources?"
                        value={field.state.value ?? ''}
                        onChange={(v: number | string) =>
                          field.handleChange(v === '' ? null : Number(v))
                        }
                      />
                    )}
                  </form.AppField>
                </>
              ) : null}

              {situationNeedsDegree(situation) ? (
                <>
                  <form.AppField name="degreeName">
                    {(field) => (
                      <field.Text
                        label="Completed degree"
                        placeholder="e.g. A.A. in General Studies"
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="degreeInstitution">
                    {(field) => (
                      <field.Text label="Institution" placeholder="e.g. Arizona State University" />
                    )}
                  </form.AppField>
                </>
              ) : null}

              {situationNeedsAdditionalCredits(situation) ? (
                <>
                  <form.AppField name="additionalCredits">
                    {(field) => (
                      <field.Number
                        label="Additional credits (not applied to the associate)"
                        value={field.state.value ?? ''}
                        onChange={(v: number | string) =>
                          field.handleChange(v === '' ? null : Number(v))
                        }
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="additionalCreditsSource">
                    {(field) => (
                      <field.Select
                        label="Where were they earned?"
                        placeholder="Select…"
                        options={SOURCE_SELECT_OPTIONS}
                      />
                    )}
                  </form.AppField>
                </>
              ) : null}
            </div>
          )}
        </form.Subscribe>
      </div>
    );
  },
});
